// @flow

import React, {useEffect, useState, useCallback, useRef, memo} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectUnits,
  selectIsFeedLive,
  setFeedLive,
  updateRequests,
  selectRequests
} from '../../reduxStore/modules/feed';
import { selectUser } from '../../reduxStore/modules/user';
import { 
  setIsAppLoading,
  setSnackMessage,
  setLoadingModule 
} from '../../reduxStore/modules/common';
import { useI18n } from '../../contexts/I18nContext';
import { log } from '../../utils/jsUtils';
import classNames from 'classnames';
import moment from 'moment';
import * as R from 'ramda';
import {
  formatPhoneNumberForFrontend,
  formatPhoneNumberForBackend,
  validateEmail
} from '../../utils/jsUtils';
import modelUtils from '../../utils/modelUtils';
import { useAddTagToCustomerMutation, useRemoveTagFromCustomerMutation } from '../../features/api/tagsApi';
import { useStore } from 'react-redux';

import Hidden from '@mui/material/Hidden';
import Grid from '@mui/material/Grid';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import EditIcon from '@mui/icons-material/Edit';

import UnreadBadge from '../SharedComponents/UnreadBadge';
import TagRenderer from '../SharedComponents/TagRenderer';
import EditTenantDialog from '../Tenants/EditTenantDialog';
import SPBackend from '../../services/SPBackend';

import styles from './Styles/MemberContent.Style';
import combineStyles from '../../utils/stylesUtils';
import commonFormElementsStyles from '../Styles/CommonFormElements.Style';
import theme from '../../theme';
import { REF_STATUSES } from '../../models/Request';
import type { Task, Unit, Tag, Tenant } from '../../services/SPBackend';
import { useTheme } from '@mui/material/styles';
import memberContentStyles from './Styles/MemberContent.Style';
import commonListTableStyles from '../Styles/CommonListTable.Style';
import commonFeedTableStyles from './Styles/CommonFeedTable.Style';
import feedRowStyles from './Styles/FeedRow.Style';
import feedMobileStyles from './Styles/CommonFeedMobile.Style';
import commonCollapsibleListTableStyles from '../Styles/CommonCollapsibleListTable.Style';

// Debug flag for logging
const DEBUG_ENABLED = false; // process.env.NODE_ENV !== 'production';

type Props = {
  request: Task,
  markInProgress: Function,
  triggerOpenAssignTaskDialog: Function,
  goToRequest: Function,
  units: Array<Unit>,
  readonly: Boolean,
  updateTaskCallback: Function,
  setTenant?: Function,
  useGridLayout?: boolean,  // Enable CSS Grid layout mode for desktop
  mobileOnly?: boolean  // When true, only render mobile layout (skip desktop section)
}

// Memoization comparison function to prevent unnecessary re-renders
const areEqual = (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.request._id === nextProps.request._id &&
    prevProps.readonly === nextProps.readonly &&
    prevProps.useGridLayout === nextProps.useGridLayout &&
    prevProps.mobileOnly === nextProps.mobileOnly &&
    JSON.stringify(prevProps.request.lookupTenant?.Tags) === JSON.stringify(nextProps.request.lookupTenant?.Tags) &&
    prevProps.request.UnreadCount === nextProps.request.UnreadCount &&
    prevProps.request.Status === nextProps.request.Status &&
    prevProps.request.Issue === nextProps.request.Issue &&
    prevProps.request.UpdatedDt === nextProps.request.UpdatedDt &&
    prevProps.request.Assignee?._id === nextProps.request.Assignee?._id
  );
};

const FeedRow = (props) => {
  const {
    goToRequest,
    request,
    triggerOpenAssignTaskDialog,
    readonly,
    updateTaskCallback,
    setTenant,
    useGridLayout = false,
    mobileOnly = false
  } = props;

  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const i18n = useI18n();
  
  // Redux selectors
  const user = useSelector(selectUser);
  const units = useSelector(selectUnits);
  const isFeedLive = useSelector(selectIsFeedLive);
  const requests = useSelector(selectRequests);
  
  // Force update counter for TagRenderer re-renders
  const [tagUpdateCounter, setTagUpdateCounter] = useState(0);
  const prevTagsStringRef = useRef('');
  
  // RTK Query mutations
  const [addTagToCustomer, { isLoading: isAddingTag }] = useAddTagToCustomerMutation();
  const [removeTagFromCustomer, { isLoading: isRemovingTag }] = useRemoveTagFromCustomerMutation();
  
  const [hover, setHover] = useState(false);
  const [openEditTenantDialog, setOpenEditTenantDialog] = useState(false);
  const [isLoadingAssignTask, setLoadingAssignTask] = useState(false);
  const [unit, setUnit] = useState(null);

  const store = useStore();
  
  // Enhanced debug logging
  const logDebug = useCallback((message, ...args) => {
    if (DEBUG_ENABLED) {
      log(`[FeedRow:${request?._id?.slice(0, 6)}] ${message}`, ...args);
    }
  }, [request]);

  // Track changes to request.lookupTenant.Tags efficiently
  useEffect(() => {
    const currentTags = request?.lookupTenant?.Tags || [];
    // Create a copy before sorting to avoid mutating a potentially frozen array
    const currentTagsString = JSON.stringify([...currentTags].map(id => String(id)).sort());
    
    if (prevTagsStringRef.current !== currentTagsString) {
      const prevTags = prevTagsStringRef.current ? JSON.parse(prevTagsStringRef.current) : [];
      const prevTagsCount = prevTags.length;
      const currentTagsCount = currentTags.length;
      
      logDebug('Tags changed, updating counter', { 
        prevCount: prevTagsCount, 
        newCount: currentTagsCount,
        added: currentTagsCount > prevTagsCount,
        removed: currentTagsCount < prevTagsCount
      });
      
      prevTagsStringRef.current = currentTagsString;
      setTagUpdateCounter(prev => prev + 1);
    }
  }, [request?.lookupTenant?.Tags, logDebug]);
  
  // Efficient tag removal handler - updates tenant state and triggers UI update
  const handleTagRemoved = useCallback((tag) => {
    if (!tag?._id) return;

    logDebug('Tag removed, updating tenant and incrementing counter', { tagId: tag._id });

    // Update tenant's Tags in parent state (for /member/xxx local state updates)
    if (setTenant && request?.lookupTenant) {
      setTenant(prevTenant => ({
        ...prevTenant,
        Tags: (prevTenant?.Tags || []).filter(id => String(id) !== String(tag._id))
      }));
    }

    // Increment counter to force re-render
    setTagUpdateCounter(prev => prev + 1);
  }, [logDebug, setTenant, request?.lookupTenant]);

  // Efficient tag addition handler - updates tenant state and triggers UI update
  const handleTagAdded = useCallback((tag) => {
    if (!tag?._id) return;

    logDebug('Tag added, updating tenant and incrementing counter', { tagId: tag._id });

    // Update tenant's Tags in parent state (for /member/xxx local state updates)
    if (setTenant && request?.lookupTenant) {
      const existingTags = request.lookupTenant.Tags || [];
      if (!existingTags.some(id => String(id) === String(tag._id))) {
        setTenant(prevTenant => ({
          ...prevTenant,
          Tags: [...(prevTenant?.Tags || []), tag._id]
        }));
      }
    }

    // Increment counter to force re-render
    setTagUpdateCounter(prev => prev + 1);
  }, [logDebug, setTenant, request?.lookupTenant]);

  const handleEditTenant = (tenant: Tenant, moveTasks: boolean, callback: Function = null) => {
    const formattedNb = formatPhoneNumberForBackend(tenant.Phone);
    
    if (tenant.Email !== '' && !validateEmail(tenant.Email)) {
      dispatch(setSnackMessage({
        message: 'Invalid email.',
        level: 'error',
        duration: 3000
      }));
      return;
    }
    
    if (!formattedNb) {
      dispatch(setSnackMessage({
        message: 'Invalid phone number.',
        level: 'error',
        duration: 3000
      }));
      return;
    }

    const payload = {
      ...tenant,
      Phone: formattedNb,
      moveTasks,
      // Filter out null, undefined, and temporary tag IDs
      Tags: (tenant.Tags || []).filter(tagId =>
        tagId != null && !String(tagId).startsWith('temp_')
      )
    };

    dispatch(setLoadingModule('editTenant', true));
    
    SPBackend.editTenant(payload, user)
      .then(response => {
        log('[handleEditTenant] response: ', response);
        const updatedTenant = response.data?.tenant;

        // Handle empty response gracefully
        if (!updatedTenant) {
          dispatch(setSnackMessage({
            message: 'Failed to update tenant. Please try again.',
            level: 'error',
            duration: 3000
          }));
          dispatch(setLoadingModule('editTenant', false));
          return;
        }

        // Deep clone updatedTenant to ensure all nested properties are mutable
        const mutableTenant = {
          ...updatedTenant,
          Tags: updatedTenant.Tags ? [...updatedTenant.Tags] : [],
          unitsDocs: updatedTenant.unitsDocs ? [...updatedTenant.unitsDocs.map(unit => ({ ...unit }))] : []
        };
        
        if (callback) {
          callback({
            ...request,
            lookupTenant: mutableTenant
          });
        }
        
        if (typeof updateTaskCallback !== 'undefined') {
          updateTaskCallback({
            ...request,
            lookupTenant: mutableTenant
          });
        }
        
        dispatch(updateRequests([{
          ...request,
          lookupTenant: mutableTenant
        }]));
        
        dispatch(setSnackMessage({
          message: `Tenant updated`,
          level: 'success',
          duration: 3000
        }));
        
        dispatch(setLoadingModule('editTenant', false));
      })
      .catch(error => {
        log('[handleEditTenant] error', error);
        dispatch(setSnackMessage({
          message: error.message || 'An error occured. Please try again.',
          level: 'error',
          duration: 3000
        }));
        dispatch(setLoadingModule('editTenant', false));
      });
  };

  const handleRenameTask = (taskId) => (updatedText) => {
    dispatch(setIsAppLoading(true));
    SPBackend.renameTask(
      taskId,
      updatedText,
      user
    ).then(response => {
      // update assignee for each task in the feed
      // log('[handleRenameTask] response: ', response);
      dispatch(updateRequests([response.data.task]));
      // log('[FeedRow.handleRenameTask] updateTaskCallback: ', updateTaskCallback);
      if (typeof updateTaskCallback !== 'undefined') {
        updateTaskCallback(response.data.task);
      }
      dispatch(setSnackMessage({
        message: response.data.message,
        level: response.data.success ? 'success' : 'error',
        duration: 2000
      }));
      dispatch(setIsAppLoading(false));
    })
    .catch(e => {
      log('[handleRenameTask] error: ', e);
      dispatch(setSnackMessage({
        message: 'Something went wrong. Please try again.',
        level: 'error',
        duration: 2000
      }));
      dispatch(setIsAppLoading(false));
    });
  };

  useEffect(() => {
    if (units && request) {
      // Check if we need to update the unit
      const u = R.find(R.propEq('RoomKey', request.RoomKey))(units);
      const needsUpdate = 
        (u && !unit) || 
        (!u && unit) || 
        (u && unit && u.RoomKey !== unit.RoomKey);
      
      if (needsUpdate) {
        log('[useEffect] Updating unit for request:', { 
          requestId: request._id,
          roomKey: request.RoomKey,
          foundUnit: !!u
        });
        
        setUnit(u || null);
      }
    }
  }, [request?.RoomKey, units, unit]);

  // Make editTenant handler stable with useCallback
  const handleEditTenantDialog = useCallback((e) => {
    e.stopPropagation();
    setOpenEditTenantDialog(true);
  }, []);

  // Row mouse event handlers with proper dependencies
  const handleRowMouseEnter = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!readonly && isFeedLive) {
      setHover(true);
    }
  }, [readonly, isFeedLive]);

  const handleRowMouseLeave = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!readonly && isFeedLive) {
      setHover(false);
    }
  }, [readonly, isFeedLive]);

  // console.log('[FeedRow][render] props: ', props);

  return (
    <>
      {/* Mobile Version - shows below 1000px */}
      <Grid
        item
        sx={{
          ...feedMobileStyles.mobileFeedItem(theme),
          display: 'flex',
          '@media (min-width: 1000px)': {
            display: 'none'
          }
        }}
        onClick={!readonly ? (e) => goToRequest(request._id)(e) : undefined}
      >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            sx={feedMobileStyles.mobileRowCtr(theme)}
          >
            <Grid
              item
              sx={feedMobileStyles.mobileLeftCol(theme)}
            >
              <Box sx={feedMobileStyles.topTitleCtr(theme)}>
                {!readonly && typeof request.UnreadCount === 'number' && request.UnreadCount > 0 && (
                  <UnreadBadge />
                )}
                
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden'
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => { e.stopPropagation(); goToRequest(request._id)(e); }}
                  >
                    {request.lookupTenant?.FirstName && request.lookupTenant.FirstName !== 'Guest'
                      ? `${request.lookupTenant.FirstName} ${request.lookupTenant.LastName || ''}`
                      : formatPhoneNumberForFrontend(request.From)}
                  </span>
                  <EditIcon
                    sx={{ fontSize: 16, marginLeft: 0.5, flexShrink: 0, cursor: 'pointer' }}
                    onClick={handleEditTenantDialog}
                  />
                </Typography>
              </Box>

              <Box sx={{ marginTop: theme.spacing(0.5), width: '100%', overflow: 'hidden' }}>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(1),
                    cursor: 'pointer',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                  onClick={!readonly ? (e) => goToRequest(request._id)(e) : undefined}
                >
                  {request.Issue}
                </Typography>
              </Box>

              <Box sx={{
                marginTop: theme.spacing(0.5),
                width: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                // Thin scrollbar styling for mobile
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2 }
              }}>
                {request.lookupTenant && (
                  <TagRenderer
                    key={`tag-renderer-mobile-${request._id}-${tagUpdateCounter}`}
                    customerId={request.lookupTenant._id}
                    tagsIds={request.lookupTenant?.Tags}
                    readonly={false}
                    debug={DEBUG_ENABLED}
                    onTagRemoved={handleTagRemoved}
                    onTagAdded={handleTagAdded}
                    tagUpdateCounter={tagUpdateCounter}
                  />
                )}
              </Box>
            </Grid>

            <Grid
              item
              sx={feedMobileStyles.mobileRightCol(theme)}
            >
              <Typography variant="caption" sx={feedMobileStyles.mobileTimestamp(theme)}>
                {moment(request.UpdatedDt).fromNow()}
              </Typography>
              
              {request.Assignee && request.Assignee.FirstName && request.Assignee.LastName && (
                <Avatar sx={feedMobileStyles.mobileStaffAvatar(theme)}>
                  {request.Assignee.FirstName.charAt(0)}{request.Assignee.LastName.charAt(0)}
                </Avatar>
              )}
            </Grid>
          </Grid>
        </Grid>

      {/* Desktop Version - only render if not mobileOnly */}
      {!mobileOnly && (useGridLayout ? (
        <Box
          sx={{
            ...commonFeedTableStyles.feedTableGridRow(theme),
            cursor: readonly ? 'default' : 'pointer'
          }}
          className={hover ? 'hover' : ''}
          onClick={!readonly ? (e) => goToRequest(request._id)(e) : undefined}
          onMouseEnter={handleRowMouseEnter}
          onMouseLeave={handleRowMouseLeave}
        >
          {/* Customer Cell */}
          <Box sx={{
            ...commonFeedTableStyles.feedTableGridCell(theme),
            ...commonFeedTableStyles.feedTableGridCellFirst(theme)
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', width: '100%' }}>
              {/* Fixed width container for badge/spacing */}
              <Box sx={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginRight: theme.spacing(1) }}>
                {!readonly && typeof request.UnreadCount === 'number' && request.UnreadCount > 0 && (
                  <UnreadBadge />
                )}
              </Box>
              {/* Tenant name */}
              <Box
                onClick={handleEditTenantDialog}
                sx={{
                  ...memberContentStyles.editTenantCtr(theme),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                  flex: 1,
                  minWidth: 0
                }}
              >
                <Box
                  component="span"
                  sx={{
                    ...commonListTableStyles.textOverflow(theme),
                    ...memberContentStyles.messageHeader(),
                    display: 'block',
                    maxWidth: '100%'
                  }}
                >
                  {request.lookupTenant?.FirstName && request.lookupTenant.FirstName !== 'Guest'
                    ? request.lookupTenant.LastName
                      ? request.lookupTenant.FirstName + ' ' + request.lookupTenant.LastName
                      : request.lookupTenant.FirstName
                    : formatPhoneNumberForFrontend(request.From)}
                </Box>
                {(hover || readonly) && (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <EditIcon sx={memberContentStyles.editIcon(theme)} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Tag Cell */}
          <Box sx={{
            ...commonFeedTableStyles.feedTableGridCell(theme),
            ...commonFeedTableStyles.tagGridCell(theme)
          }}>
            {request.lookupTenant && (
              <TagRenderer
                key={`tag-renderer-${request._id}-${tagUpdateCounter}`}
                customerId={request.lookupTenant._id}
                tagsIds={request.lookupTenant?.Tags}
                readonly={false}
                debug={DEBUG_ENABLED}
                onTagRemoved={handleTagRemoved}
                onTagAdded={handleTagAdded}
                tagUpdateCounter={tagUpdateCounter}
              />
            )}
          </Box>

          {/* Subject Cell */}
          <Box sx={commonFeedTableStyles.feedTableGridCell(theme)}>
            <Tooltip
              aria-label={request.Issue}
              placement='top'
              title={request.Issue && request.Issue.length > 50 ? request.Issue : ''}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  width: '100%'
                }}
              >
                {request.Issue}
              </Box>
            </Tooltip>
          </Box>

          {/* Staff Cell */}
          <Box sx={commonFeedTableStyles.feedTableGridCell(theme)}>
            <Box sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {request.Assignee?.FirstName && request.Assignee?.LastName
                ? `${request.Assignee.FirstName} ${request.Assignee.LastName}`
                : ''}
            </Box>
          </Box>

          {/* Date Cell - last cell in grid */}
          <Box sx={{
            ...commonFeedTableStyles.feedTableGridCell(theme),
            justifyContent: 'flex-end',
            paddingRight: theme.spacing(3)  // Right margin on row
          }}>
            {request.UpdatedDt
              ? moment().diff(request.UpdatedDt, 'days') > 1
                ? moment(request.UpdatedDt).format('MMM D')
                : moment(request.UpdatedDt).from(moment())
              : moment().diff(request.Date, 'days') > 1
                ? moment(request.Date).format('MMM D')
                : moment(request.Date).from(moment())
            }
          </Box>

          {/* Action overlay - positioned on row, not in a cell */}
          {!readonly && request.Status.toLowerCase() !== REF_STATUSES.INFO && (
            <Box sx={{
              ...commonFeedTableStyles.actionOverlay(theme),
              ...(hover ? commonFeedTableStyles.actionOverlayVisible(theme) : {})
            }}>
              <Tooltip title="Assign" placement="top">
                {isLoadingAssignTask ? (
                  <CircularProgress
                    sx={{
                      ...memberContentStyles.iconSpinner(),
                      ...commonCollapsibleListTableStyles.rowActionBtn(theme)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size={theme.layout.listTable.rowHeight}
                  />
                ) : (
                  <IconButton
                    aria-label="Assign"
                    sx={{ ...commonCollapsibleListTableStyles.rowActionBtn(theme) }}
                    onClick={triggerOpenAssignTaskDialog(request)}
                  >
                    <i
                      className="icon-assign"
                      style={{ ...memberContentStyles.icon(), color: 'inherit' }}
                    />
                  </IconButton>
                )}
              </Tooltip>
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop Version - Original Flex Layout (fallback) */
        <Hidden smDown>
          <TableRow
            className={hover ? 'hover' : null}
            sx={{
              ...commonListTableStyles.feedTableRow(theme),
              ...(readonly ? {} : commonCollapsibleListTableStyles.cursorPointer())
            }}
            onClick={!readonly ? (e) => goToRequest(request._id)(e) : undefined}
            onMouseEnter={handleRowMouseEnter}
            onMouseLeave={handleRowMouseLeave}
          >
            <TableCell
              align="left"
              component="td"
              scope="row"
              sx={{
                ...commonFeedTableStyles.cell(theme),
                ...commonFeedTableStyles.customerNameCell(theme),
                ...commonListTableStyles.clickableCellMargin(theme)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <Box sx={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginLeft: theme.spacing(1), marginRight: theme.spacing(1) }}>
                  {!readonly && typeof request.UnreadCount === 'number' && request.UnreadCount > 0 && (
                    <UnreadBadge />
                  )}
                </Box>
                <Box
                  onClick={handleEditTenantDialog}
                  sx={{
                    ...memberContentStyles.editTenantCtr(theme),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      ...commonListTableStyles.textOverflow(theme),
                      ...memberContentStyles.messageHeader(),
                      display: 'block',
                      maxWidth: '100%'
                    }}
                  >
                    {request.lookupTenant?.FirstName && request.lookupTenant.FirstName !== 'Guest'
                      ? request.lookupTenant.LastName
                        ? request.lookupTenant.FirstName + ' ' + request.lookupTenant.LastName
                        : request.lookupTenant.FirstName
                      : formatPhoneNumberForFrontend(request.From)}
                  </Box>
                  {(hover || readonly) && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      <EditIcon sx={memberContentStyles.editIcon(theme)} />
                    </Box>
                  )}
                </Box>
              </Box>
            </TableCell>

            <TableCell
              sx={{
                ...commonFeedTableStyles.cell(theme),
                ...commonFeedTableStyles.tagCell(theme),
              }}
              onClick={!readonly ? (e) => goToRequest(request._id)(e) : undefined}
            >
              {request.lookupTenant && (
                <TagRenderer
                  key={`tag-renderer-${request._id}-${tagUpdateCounter}`}
                  customerId={request.lookupTenant._id}
                  tagsIds={request.lookupTenant?.Tags}
                  readonly={false}
                  debug={DEBUG_ENABLED}
                  onTagRemoved={handleTagRemoved}
                  onTagAdded={handleTagAdded}
                  tagUpdateCounter={tagUpdateCounter}
                />
              )}
            </TableCell>

            <TableCell
              align='left'
              sx={{
                ...commonFeedTableStyles.cell(theme),
                ...commonFeedTableStyles.requestCell(theme)
              }}
            >
              <Tooltip
                aria-label={request.Issue}
                sx={{ tooltip: { ...commonCollapsibleListTableStyles.taskNameTooltip(theme) } }}
                placement='top'
                title={request.Issue && request.Issue.length > 50 ? request.Issue : ''}
              >
                <span className={classNames(
                  commonFeedTableStyles.textOverflow(theme),
                  memberContentStyles.messagePreview()
                )}>
                  {request.Issue}
                </span>
              </Tooltip>
            </TableCell>

            <TableCell
              align="left"
              sx={{
                ...commonFeedTableStyles.cell(theme),
                ...commonFeedTableStyles.assigneeCell(theme)
              }}
            >
              <Box sx={{ ...commonListTableStyles.textOverflow(theme) }}>
                {request.Assignee?.FirstName && request.Assignee?.LastName
                  ? `${request.Assignee.FirstName} ${request.Assignee.LastName}`
                  : ''}
              </Box>
            </TableCell>

            <TableCell
              align='right'
              className={classNames('hideOnRowHover', 'lastChild')}
              sx={{
                ...commonFeedTableStyles.cell(theme),
                ...commonFeedTableStyles.dateCell(theme),
              }}
            >
              {request.UpdatedDt
                ? moment().diff(request.UpdatedDt, 'days') > 1
                  ? moment(request.UpdatedDt).format('MMM D')
                  : moment(request.UpdatedDt).from(moment())
                : moment().diff(request.Date, 'days') > 1
                  ? moment(request.Date).format('MMM D')
                  : moment(request.Date).from(moment())
              }
            </TableCell>

            {!readonly && (
              <TableCell
                align="right"
                className="actionCell lastChild"
                sx={{
                  ...commonFeedTableStyles.cell(theme),
                  ...commonFeedTableStyles.actionCell(theme),
                }}
              >
                {request.Status.toLowerCase() !== REF_STATUSES.INFO && (
                  <Tooltip title="Assign" placement="top">
                    {isLoadingAssignTask ? (
                      <CircularProgress
                        sx={{
                          ...memberContentStyles.iconSpinner(),
                          ...commonCollapsibleListTableStyles.rowActionBtn(theme)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size={theme.layout.listTable.rowHeight}
                      />
                    ) : (
                      <IconButton
                        aria-label="Assign"
                        sx={{ ...commonCollapsibleListTableStyles.rowActionBtn(theme) }}
                        onClick={triggerOpenAssignTaskDialog(request)}
                      >
                        <i
                          className="icon-assign"
                          style={{ ...memberContentStyles.icon(), color: 'inherit' }}
                        />
                      </IconButton>
                    )}
                  </Tooltip>
                )}
              </TableCell>
            )}
          </TableRow>
        </Hidden>
      ))}

      <EditTenantDialog
        currTenant={request.lookupTenant}
        open={openEditTenantDialog}
        setOpen={setOpenEditTenantDialog}
        triggerAction={handleEditTenant}
      />
    </>
  );
};

export default memo(FeedRow, areEqual);